import * as Sentry from '@sentry/browser'
import Sortable from 'sortablejs'

const EXPECTED_REJECTIONS = [403, 404, 409, 422]
const DRAG_CLICK_SUPPRESSION_MS = 500
const ACTION_EXECUTED_EVENT = 'action-executed'
const ACTION_DIALOG_CANCELLED_EVENT = 'resourcerer-action-dialog-cancelled'
const VIRTUAL_TRANSITION_ACTION = 'transition_virtual_lane'
const DROP_PLACEHOLDER_CLASS = 'task-board-drop-placeholder'
const DRAG_CHOSEN_CLASS = 'task-board-drag-chosen'
const DRAGGING_CLASS = 'task-board-dragging'
const DRAG_STATE_CLASSES = [DROP_PLACEHOLDER_CLASS, DRAG_CHOSEN_CLASS, DRAGGING_CLASS]
const FALLBACK_CLASS = 'sortable-fallback'
const CARD_SELECTOR = `[data-task-board-card]:not(.${FALLBACK_CLASS})`
const DIRECT_CARD_SELECTOR = `:scope > ${CARD_SELECTOR}`
const SORTABLE_CONTROL_SELECTOR =
  '[data-task-board-move-controls], [data-task-board-move-controls] *'
const VIEWPORT_BOTTOM_GAP = 16
// Leave room for a complete card between the lane header and create footer.
const MINIMUM_BOARD_HEIGHT = 384

/**
 * Optimistic transport for the administrative task board.
 *
 * Sortable owns the DOM only until the Livewire call settles. The server then
 * renders the complete authorization-filtered projection again; failures first
 * restore the captured visible order and then request that same authoritative
 * projection. Only the Livewire component id is retained, never its $wire proxy.
 */
export default function taskBoardKanban({
  errorTitle,
  errorMessage,
  recoveryUrl,
  virtual = false
}) {
  let wireId = null
  let instances = []
  let snapshot = []
  let inFlightOrder = []
  let pendingControlFocus = null
  let stopWatchingCommits = null
  let stopWatchingRequests = null
  let stopWatchingMorphs = null
  let stopSuppressingClick = null
  let actionExecutedHandler = null
  let actionDialogCancelledHandler = null
  let activeDropToken = null
  let activeVirtualAction = null
  let activeDrag = null
  let viewportResizeHandler = null
  let viewportResizeObserver = null
  let viewportScrollTarget = null
  let lanePagination = new Map()
  let destroyed = false

  return {
    saving: false,

    init() {
      destroyed = false
      const component = this.$root?.closest?.('[wire\\:id]')
      wireId = component?.getAttribute('wire:id') ?? null

      this.watchLivewireCommits()
      this.watchLivewireRequests()
      this.watchLivewireMorphs()
      this.watchActionDialogLifecycle()
      this.watchViewport()
      this.$nextTick(() => this.boot())
    },

    destroy() {
      destroyed = true
      activeDropToken = null
      activeVirtualAction = null
      activeDrag = null
      stopWatchingCommits?.()
      stopWatchingCommits = null
      stopWatchingRequests?.()
      stopWatchingRequests = null
      stopWatchingMorphs?.()
      stopWatchingMorphs = null
      this.stopWatchingActionDialogLifecycle()
      stopSuppressingClick?.()
      stopSuppressingClick = null
      this.stopWatchingViewport()
      this.stopLanePagination()
      lanePagination.clear()
      this.destroySortables()
      snapshot = []
      inFlightOrder = []
      pendingControlFocus = null
      wireId = null
    },

    boardRoot() {
      if (!wireId) return null

      const component = document.querySelector(`[wire\\:id="${wireId}"]`)

      return component?.querySelector?.('[data-task-board-root]') ?? null
    },

    livewire() {
      return wireId && window.Livewire ? (window.Livewire.find(wireId) || null) : null
    },

    boot({ pagination = false } = {}) {
      if (destroyed) return

      const root = this.boardRoot()
      const lists = [...(root?.querySelectorAll('[data-task-board-lane-list]') ?? [])]
      instances = instances.filter(instance => {
        if (lists.includes(instance.el)) return true

        instance.destroy()

        return false
      })
      if (!pagination) {
        if (!activeDrag) this.clearDragStateClasses()
        this.syncViewportHeight()
        this.revealCreatedCard()
      }
      this.setSortablesDisabled(this.saving)
      this.watchLanePagination()

      if (!root?.querySelector(`${CARD_SELECTOR}[data-task-board-movable="true"]`)) {
        this.syncEmptyStates()
        if (!pagination) this.restorePendingControlFocus()

        return
      }

      const boardGroup = this.isVirtualBoard()
        ? (wireId ? `virtual-task-board-${wireId}` : null)
        : this.positiveInteger(root.dataset.boardId)
      if (boardGroup === null) return

      lists.forEach(list => {
        const existing = instances.find(instance => instance.el === list)
        const group = `task-board-${boardGroup}`
        if (existing) {
          if ((existing.options.group?.name ?? existing.options.group) !== group) existing.option('group', group)

          return
        }
        instances.push(new Sortable(list, {
          scroll: true,
          bubbleScroll: true,
          forceAutoScrollFallback: true,
          scrollSensitivity: 80,
          scrollSpeed: 14,
          fallbackOnBody: false,
          animation: 150,
          draggable: '[data-task-board-movable="true"]',
          group,
          fallbackClass: FALLBACK_CLASS,
          ghostClass: DROP_PLACEHOLDER_CLASS,
          chosenClass: DRAG_CHOSEN_CLASS,
          dragClass: DRAGGING_CLASS,
          fallbackTolerance: 5,
          delay: 200,
          delayOnTouchOnly: true,
          touchStartThreshold: 5,
          disabled: this.saving,
          filter: SORTABLE_CONTROL_SELECTOR,
          preventOnFilter: false,
          onStart: event => {
            if (!this.saving) snapshot = this.captureVisibleOrder()
            const taskId = this.positiveInteger(event?.item?.dataset?.taskId)
            const sourceLane = this.laneIdentifier(event?.from)
            activeDrag = taskId !== null && typeof sourceLane === 'string'
              ? { leftSource: false, sourceLane, taskId }
              : null
          },
          onChange: () => this.syncEmptyStates(),
          onMove: event => this.allowsProjectedDrop(event?.dragged, event?.to),
          onEnd: event => {
            const taskId = this.positiveInteger(event?.item?.dataset?.taskId)
            const returnedToSource = activeDrag?.taskId === taskId &&
              activeDrag.leftSource &&
              this.laneIdentifier(event?.to) === activeDrag.sourceLane
            if (activeDrag) lanePagination.forEach(state => state.consumeScroll())
            activeDrag = null
            this.clearDragStateClasses()
            this.syncEmptyStates()
            this.suppressNextClick(event?.item)

            if (returnedToSource) {
              this.restoreVisibleOrder()
              snapshot = []

              return
            }
            if (
              event?.from === event?.to &&
              event?.oldDraggableIndex === event?.newDraggableIndex
            ) {
              snapshot = []

              return
            }

            this.persistDrop(event)
          }
        }))
      })

      this.syncEmptyStates()
      if (!pagination) this.restorePendingControlFocus()
    },

    lanePaginationGeneration() {
      const value = this.boardRoot()?.closest('[data-task-workspace]')?.dataset.clientGen
      if (typeof value !== 'string' || !/^\d+$/.test(value)) return null

      const generation = Number(value)

      return Number.isSafeInteger(generation) ? generation : null
    },

    stopLanePagination() {
      lanePagination.forEach(state => state.stop?.())
    },

    syncLanePaginationFeedback(token) {
      lanePagination.forEach(state => {
        if (state.pending === token) state.syncFeedback?.()
      })
    },

    watchLanePagination() {
      const previous = lanePagination
      lanePagination = new Map()
      const generation = this.lanePaginationGeneration()
      const root = this.boardRoot()
      if (!this.isVirtualBoard() || generation === null || !root) {
        previous.forEach(state => state.stop?.())

        return
      }

      root.querySelectorAll('[data-task-board-lane-list]').forEach(list => {
        let button = list.querySelector('[data-test="tasks.lane-load-more"]')
        const lane = this.laneIdentifier(list)
        if (!button || typeof lane !== 'string') return
        let sentinel = button.closest('[data-test="tasks.lane-pagination"]') ?? button

        const old = previous.get(lane)
        const boardId = root.dataset.boardId
        const boardKind = root.dataset.taskBoardKind
        const sameBoard = old?.root === root && old.boardId === boardId && old.boardKind === boardKind
        if (sameBoard && old.generation === generation && old.list === list) {
          lanePagination.set(lane, old)
          old.retarget(button, sentinel)

          return
        }
        const state = {
          root,
          boardId,
          boardKind,
          generation,
          list,
          pending: sameBoard && old.generation === generation ? old.pending : null
        }
        lanePagination.set(lane, state)
        let active = true
        let intentional = false
        let armed = false
        let intersecting = false
        const current = () => active && !destroyed &&
          lanePagination.get(lane) === state &&
          this.lanePaginationGeneration() === generation &&
          this.boardRoot() === root && root.dataset.boardId === boardId && root.dataset.taskBoardKind === boardKind &&
          root.contains(list) && list.contains(button)
        const consumeScroll = () => {
          intentional = false
          armed = false
        }
        const load = async() => {
          if (!current() || activeDrag || !armed || !intersecting || state.pending?.active || button.disabled || this.saving) return

          const wire = this.livewire()
          if (typeof wire?.loadMoreLane !== 'function') return

          consumeScroll()
          const token = { active: true, automatic: true }
          state.pending = token
          state.syncFeedback()
          try {
            await wire.loadMoreLane(lane, generation)
          } catch {
            token.failed = true
          } finally {
            token.active = false
            this.syncLanePaginationFeedback(token)
          }
        }
        const onIntent = event => {
          if (!current() || state.pending?.active) return
          if (event.type === 'wheel' && event.deltaY <= 0) return
          if (event.type === 'keydown' && (
            !['ArrowDown', 'PageDown', 'End', ' '].includes(event.key) ||
            event.target?.closest('button, a, input, textarea, select, [contenteditable]')
          )) return

          intentional = true
        }
        const onScroll = () => {
          if (!current() || activeDrag || state.pending?.active || !intentional || list.scrollHeight <= list.clientHeight) return

          intentional = false
          armed = true
          load()
        }
        const onClick = event => {
          if (!current() || button.disabled) return
          if (state.pending?.active) {
            event.preventDefault()
            event.stopImmediatePropagation()

            return
          }
          consumeScroll()
          state.pending = { active: true }
          state.syncFeedback()
        }
        const observer = typeof window.IntersectionObserver === 'function'
          ? new window.IntersectionObserver(entries => {
            if (!current()) return
            entries.forEach(entry => {
              if (entry.target !== sentinel) return
              intersecting = entry.isIntersecting
              load()
            })
          }, { root: list, rootMargin: '0px 0px 80px 0px' })
          : null
        const intentEvents = ['wheel', 'touchmove', 'keydown', 'pointerdown']
        intentEvents.forEach(name => list.addEventListener(name, onIntent, { passive: true }))
        list.addEventListener('scroll', onScroll, { passive: true })
        button.addEventListener('click', onClick, true)
        state.syncFeedback = () => {
          if (!current()) return
          const pending = state.pending?.active === true
          // Ignore the fallback's expansion beyond its reserved space so showing
          // it cannot turn a fitting lane into an oscillating overflow decision.
          const reservedHeight = parseFloat(window.getComputedStyle(sentinel).minHeight) || 0
          const fallbackExpansion = Math.max(0, sentinel.getBoundingClientRect().height - reservedHeight)
          const canScroll = list.scrollHeight - fallbackExpansion > list.clientHeight
          button.classList.toggle('sr-only', observer !== null && canScroll && !pending && !state.pending?.failed)
          button.setAttribute('aria-busy', pending ? 'true' : 'false')
          button.querySelector('[data-test="tasks.lane-loading"]')?.classList.toggle('hidden', !pending)
          button.querySelector('[data-test="tasks.lane-load-more-label"]')?.classList.toggle('hidden', pending)
        }
        state.syncFeedback()
        observer?.observe(sentinel)
        const geometryObserver = observer !== null && typeof window.ResizeObserver === 'function'
          ? new window.ResizeObserver(() => state.syncFeedback())
          : null
        const watchGeometry = () => {
          geometryObserver?.disconnect()
          geometryObserver?.observe(list)
          ;[...list.children].forEach(child => geometryObserver?.observe(child))
        }
        watchGeometry()
        state.consumeScroll = consumeScroll
        state.retarget = (nextButton, nextSentinel) => {
          consumeScroll()
          if (button !== nextButton) {
            button.removeEventListener('click', onClick, true)
            button = nextButton
            button.addEventListener('click', onClick, true)
          }
          if (sentinel !== nextSentinel) {
            observer?.unobserve(sentinel)
            sentinel = nextSentinel
            intersecting = false
            observer?.observe(sentinel)
          }
          state.syncFeedback()
          watchGeometry()
        }
        state.stop = () => {
          active = false
          observer?.disconnect()
          geometryObserver?.disconnect()
          intentEvents.forEach(name => list.removeEventListener(name, onIntent))
          list.removeEventListener('scroll', onScroll)
          button.removeEventListener('click', onClick, true)
        }
      })
      previous.forEach((state, lane) => {
        if (lanePagination.get(lane) !== state) state.stop?.()
      })
    },

    revealCreatedCard() {
      const card = this.boardRoot()?.querySelector('[data-task-board-focus-card]')
      if (!card) return

      card.removeAttribute('data-task-board-focus-card')
      card.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    },

    watchViewport() {
      this.stopWatchingViewport()
      viewportResizeHandler = () => {
        this.syncViewportHeight()
        lanePagination.forEach(state => state.syncFeedback?.())
      }
      window.addEventListener('resize', viewportResizeHandler)
      window.visualViewport?.addEventListener?.('resize', viewportResizeHandler)

      viewportScrollTarget = this.boardRoot()?.closest?.('.main-area') ?? null
      viewportScrollTarget?.addEventListener?.('scroll', viewportResizeHandler, { passive: true })
      if (viewportScrollTarget && typeof window.ResizeObserver === 'function') {
        viewportResizeObserver = new window.ResizeObserver(viewportResizeHandler)
        viewportResizeObserver.observe(viewportScrollTarget)

        const footer = viewportScrollTarget.querySelector(':scope > .app-footer')
        if (footer) viewportResizeObserver.observe(footer)
      }
    },

    stopWatchingViewport() {
      if (!viewportResizeHandler) return

      window.removeEventListener('resize', viewportResizeHandler)
      window.visualViewport?.removeEventListener?.('resize', viewportResizeHandler)
      viewportScrollTarget?.removeEventListener?.('scroll', viewportResizeHandler)
      viewportResizeObserver?.disconnect?.()
      viewportResizeHandler = null
      viewportResizeObserver = null
      viewportScrollTarget = null
    },

    syncViewportHeight() {
      const root = this.boardRoot()
      if (!root) return

      const mainArea = root.closest?.('.main-area')
      const isFullSize = mainArea?.dataset?.fullSize === 'true'
      let availableHeight = null
      if (mainArea) {
        const rootTop = Number(root.getBoundingClientRect().top)
        const mainTop = Number(mainArea.getBoundingClientRect().top)
        const mainHeight = Number(mainArea.clientHeight)
        const mainScrollTop = Number(mainArea.scrollTop) || 0
        const offsetInContent = rootTop - mainTop + mainScrollTop
        const footerHeight = isFullSize
          ? (Number(mainArea.querySelector(':scope > .app-footer')?.offsetHeight) || 0)
          : VIEWPORT_BOTTOM_GAP

        if (
          Number.isFinite(rootTop) &&
          Number.isFinite(mainTop) &&
          Number.isFinite(mainHeight) &&
          mainHeight > 0 &&
          Number.isFinite(offsetInContent)
        ) {
          availableHeight = mainHeight - offsetInContent - footerHeight
        }
      }

      if (availableHeight === null) {
        const rootTop = Math.max(0, Number(root.getBoundingClientRect().top))
        const viewportBottom = Number(
          (window.visualViewport?.offsetTop ?? 0) +
          (window.visualViewport?.height ?? window.innerHeight)
        )
        if (!Number.isFinite(rootTop) || !Number.isFinite(viewportBottom)) return

        availableHeight = viewportBottom - rootTop - VIEWPORT_BOTTOM_GAP
      }

      const boundedHeight = Math.max(
        MINIMUM_BOARD_HEIGHT,
        Math.floor(availableHeight)
      )
      root.style.setProperty('--task-board-viewport-height', boundedHeight + 'px')
    },

    suppressNextClick(draggedCard) {
      stopSuppressingClick?.()
      stopSuppressingClick = null

      const root = this.boardRoot()
      const taskId = this.positiveInteger(draggedCard?.dataset?.taskId)
      if (!root || taskId === null) return

      let timeout = null
      const stop = () => {
        root.removeEventListener('click', suppress, true)
        if (timeout !== null) window.clearTimeout(timeout)
        if (stopSuppressingClick === stop) stopSuppressingClick = null
      }
      const suppress = event => {
        const clickedCard = typeof event?.target?.closest === 'function'
          ? event.target.closest(CARD_SELECTOR)
          : null
        if (
          !this.isUnmodifiedPrimaryClick(event) ||
          this.positiveInteger(clickedCard?.dataset?.taskId) !== taskId
        ) return

        event.preventDefault()
        event.stopPropagation()
        stop()
      }

      root.addEventListener('click', suppress, true)
      timeout = window.setTimeout(stop, DRAG_CLICK_SUPPRESSION_MS)
      stopSuppressingClick = stop
    },

    isUnmodifiedPrimaryClick(event) {
      const button = typeof event?.button === 'number' ? event.button : 0

      return button === 0 && !event?.ctrlKey && !event?.metaKey && !event?.shiftKey && !event?.altKey
    },

    destroySortables() {
      instances.forEach(instance => instance.destroy())
      activeDrag = null
      instances = []
      this.clearDragStateClasses()
      this.syncEmptyStates()
    },

    clearDragStateClasses() {
      const root = this.boardRoot()
      if (!root) return

      root.querySelectorAll('[data-task-board-card]').forEach(card => {
        card.classList.remove(...DRAG_STATE_CLASSES)
      })
    },

    setSortablesDisabled(disabled) {
      instances.forEach(instance => instance.option('disabled', disabled))
    },

    captureVisibleOrder() {
      const root = this.boardRoot()
      if (!root) return []

      return [...root.querySelectorAll('[data-task-board-lane-list]')].map(list => ({
        laneId: this.laneIdentifier(list),
        taskIds: [...list.querySelectorAll(DIRECT_CARD_SELECTOR)]
          .map(card => card.dataset.taskId)
      }))
    },

    restoreVisibleOrder(order = snapshot) {
      const root = this.boardRoot()
      if (!root) return

      order.forEach(lane => {
        const list = [...root.querySelectorAll('[data-task-board-lane-list]')]
          .find(candidate => this.laneIdentifier(candidate) === lane.laneId)
        if (!list) return

        lane.taskIds.forEach(taskId => {
          const card = root.querySelector(`${CARD_SELECTOR}[data-task-id="${taskId}"]`)
          if (card) list.append(card)
        })
      })
      this.clearDragStateClasses()
      this.syncEmptyStates()
    },

    syncEmptyStates() {
      const root = this.boardRoot()
      if (!root) return

      root.querySelectorAll('[data-task-board-lane-list]').forEach(list => {
        const empty = list.querySelector('[data-task-board-lane-empty]')
        if (empty) empty.hidden = list.querySelector(CARD_SELECTOR) !== null
      })
    },

    async moveByControl(taskId, direction) {
      if (this.saving) return

      const parsedTaskId = this.positiveInteger(taskId)
      const root = this.boardRoot()
      if (parsedTaskId === null || !root) return

      const card = root.querySelector(`${CARD_SELECTOR}[data-task-id="${parsedTaskId}"]`)
      const sourceList = card?.closest('[data-task-board-lane-list]')
      const laneLists = [...root.querySelectorAll('[data-task-board-lane-list]')]
      const sourceLaneIndex = laneLists.indexOf(sourceList)
      if (!card || !sourceList || sourceLaneIndex < 0) return

      const cards = [...sourceList.querySelectorAll(DIRECT_CARD_SELECTOR)]
      const cardIndex = cards.indexOf(card)
      let targetList = sourceList
      let moveCard = null

      switch (direction) {
        case 'up':
          if (cardIndex <= 0) return
          moveCard = () => cards[cardIndex - 1].before(card)
          break
        case 'down':
          if (cardIndex < 0 || cardIndex >= cards.length - 1) return
          moveCard = () => cards[cardIndex + 1].after(card)
          break
        case 'previous-lane':
          if (sourceLaneIndex <= 0) return
          targetList = laneLists[sourceLaneIndex - 1]
          moveCard = () => targetList.append(card)
          break
        case 'next-lane':
          if (sourceLaneIndex >= laneLists.length - 1) return
          targetList = laneLists[sourceLaneIndex + 1]
          moveCard = () => targetList.append(card)
          break
        default:
          return
      }

      if (!this.allowsProjectedDrop(card, targetList)) return

      pendingControlFocus = { direction, taskId: parsedTaskId }
      snapshot = this.captureVisibleOrder()
      moveCard()
      this.syncEmptyStates()

      await this.persistDrop({ item: card, from: sourceList, to: targetList })
    },

    async persistDrop(event) {
      if (this.saving) {
        this.restoreVisibleOrder(inFlightOrder)

        return
      }

      if (this.isVirtualBoard()) {
        await this.persistVirtualDrop(event)

        return
      }

      const card = event?.item
      const sourceList = event?.from
      const targetList = event?.to
      const taskId = this.positiveInteger(card?.dataset?.taskId)
      const sourceLane = this.laneIdentifier(sourceList)
      const targetLane = this.laneIdentifier(targetList)
      const sourceLaneId = this.positiveInteger(sourceLane)
      const targetLaneId = this.positiveInteger(targetLane)
      const sourceIsTakeover = sourceLane === 'takeover'
      const targetIsTakeover = targetLane === 'takeover'
      const revision = this.positiveInteger(card?.dataset?.statusRevision)
      const sourceStatus = card?.dataset?.sourceStatus
      const root = this.boardRoot()
      const requiresClientGen = root?.hasAttribute?.('data-task-board-client-gen-required') === true
      const clientGen = requiresClientGen
        ? this.positiveInteger(root?.dataset?.clientGen)
        : null
      const wire = this.livewire()

      if (
        taskId === null || (!sourceIsTakeover && sourceLaneId === null) ||
        (!targetIsTakeover && targetLaneId === null) || revision === null ||
        !this.allowsProjectedDrop(card, targetList) ||
        typeof sourceStatus !== 'string' || (requiresClientGen && clientGen === null) || !wire
      ) {
        this.restoreVisibleOrder()
        this.reportError(new Error('taskBoardKanban: invalid drop snapshot'))
        await this.refreshProjection()

        return
      }

      let beforeTaskId = null
      let afterTaskId = null
      if (sourceLane === targetLane && sourceIsTakeover) {
        this.restoreVisibleOrder()
        snapshot = []

        return
      }
      if (targetIsTakeover) {
        const control = card.querySelector(
          '[data-task-board-action-target="takeover"]'
        )
        if (!control) {
          await this.recoverVirtualProjection(
            new Error('taskBoardKanban: unsupported stored takeover target')
          )

          return
        }

        this.requestVirtualTransition(control, taskId, targetLane)

        return
      }
      if (sourceLaneId !== null && sourceLaneId === targetLaneId) {
        const cards = [...targetList.querySelectorAll(DIRECT_CARD_SELECTOR)]
        const index = cards.indexOf(card)
        beforeTaskId = this.positiveInteger(cards[index + 1]?.dataset?.taskId)
        afterTaskId = this.positiveInteger(cards[index - 1]?.dataset?.taskId)
      } else {
        targetList.append(card)
        this.syncEmptyStates()
      }

      inFlightOrder = this.captureVisibleOrder()
      this.saving = true
      this.setSortablesDisabled(true)
      const dropToken = { phase: 'move' }
      activeDropToken = dropToken
      this.syncEmptyStates()

      try {
        const moveArguments = [
          taskId,
          targetIsTakeover ? 'takeover' : targetLaneId,
          beforeTaskId,
          afterTaskId,
          revision,
          sourceStatus
        ]
        if (clientGen !== null) moveArguments.push(clientGen)
        const result = await wire.moveTask(...moveArguments)

        if (result?.ok !== true) {
          if (result === null || result === undefined) {
            // Livewire already morphed the authoritative validation render.
            // Restoring the pre-request DOM here would corrupt that server state.
            this.reportError({ status: 422 })
          } else {
            await this.recoverRejectedDrop({ status: 422 }, dropToken)
          }
        }
      } catch (error) {
        await this.recoverRejectedDrop(error, dropToken)
      } finally {
        if (activeDropToken === dropToken) activeDropToken = null
        this.saving = false
        snapshot = []
        inFlightOrder = []
        this.$nextTick(() => this.boot())
      }
    },

    async persistVirtualDrop(event) {
      const card = event?.item
      const sourceList = event?.from
      const targetList = event?.to
      const taskId = this.positiveInteger(card?.dataset?.taskId)
      const sourceLane = this.laneIdentifier(sourceList)
      const targetLane = this.laneIdentifier(targetList)

      if (
        taskId === null ||
        !this.personalLane(sourceLane) ||
        !this.personalLane(targetLane)
      ) {
        await this.recoverVirtualProjection(
          new Error('taskBoardKanban: invalid virtual drop intent')
        )

        return
      }

      if (sourceLane === targetLane) {
        this.restoreVisibleOrder()
        snapshot = []

        return
      }

      if (!this.allowsProjectedDrop(card, targetList)) {
        await this.recoverVirtualProjection(
          new Error('taskBoardKanban: unsupported virtual drop target')
        )

        return
      }

      const control = card.querySelector(
        `[data-task-board-action-target="${targetLane}"]`
      )

      // Keep the card at its optimistic target until Livewire renders the
      // authoritative projection. Rejections still restore the snapshot below.
      if (!control) {
        await this.recoverVirtualProjection(
          new Error('taskBoardKanban: unsupported virtual drop target')
        )

        return
      }

      this.requestVirtualTransition(control, taskId, targetLane)
    },

    requestVirtualTransition(control, taskId, targetLane, event = null) {
      event?.preventDefault?.()
      if (this.saving) return false

      const parsedTaskId = this.positiveInteger(taskId)
      const card = control?.closest?.(CARD_SELECTOR)
      const targetColumnAttribute = control?.dataset?.taskBoardTargetColumn
      const targetColumn = targetColumnAttribute === undefined
        ? null
        : this.positiveInteger(targetColumnAttribute)
      const intent = this.virtualTransitionIntent(card, targetLane, targetColumn)
      const valid = parsedTaskId !== null &&
        this.personalLane(targetLane) &&
        this.positiveInteger(card?.dataset?.taskId) === parsedTaskId &&
        control?.dataset?.taskBoardActionTarget === targetLane &&
        (targetColumnAttribute === undefined || targetColumn !== null) &&
        this.allowsProjectedTarget(card, targetLane) &&
        intent !== null
      if (!valid) {
        this.recoverVirtualProjection(
          new Error('taskBoardKanban: invalid virtual transition control')
        )

        return false
      }

      pendingControlFocus = { targetLane, taskId: parsedTaskId }
      activeVirtualAction = {
        actionName: intent.name,
        modelIds: [...intent.modelIds],
        resourceClass: intent.resourceClass,
        sourceOrder: snapshot.length > 0 ? snapshot : this.captureVisibleOrder(),
        succeeded: false,
        boardCommitSettled: false,
        taskId: parsedTaskId
      }
      inFlightOrder = this.captureVisibleOrder()
      snapshot = []
      this.saving = true
      this.setSortablesDisabled(true)
      this.syncEmptyStates()
      control.dispatchEvent(new CustomEvent('showActionDialog', {
        bubbles: true,
        composed: true,
        detail: intent
      }))

      return true
    },

    finishVirtualDispatch() {
      this.saving = false
      activeVirtualAction = null
      snapshot = []
      inFlightOrder = []
      this.$nextTick(() => this.boot())
    },

    async recoverVirtualProjection(error, order = snapshot) {
      if (destroyed || this.saving) return

      const recoveryToken = { phase: 'refresh' }
      activeDropToken = recoveryToken
      this.saving = true
      this.setSortablesDisabled(true)
      this.restoreVisibleOrder(order)
      this.reportError(error)
      await this.refreshProjection(recoveryToken)
      if (activeDropToken === recoveryToken) activeDropToken = null
      this.finishVirtualDispatch()
    },

    virtualTransitionIntent(card, targetLane, targetColumn = null) {
      const source = card?.querySelector?.('[data-task-board-transition-data]')
      const data = this.jsonObject(source?.textContent)
      const attributes = data?.attributes
      const contextState = data?.context?.state
      if (
        !this.isPlainObject(data) ||
        data.name !== 'transition_virtual_lane' ||
        typeof data.resourceClass !== 'string' ||
        !Array.isArray(data.modelIds) ||
        data.modelIds.length !== 1 ||
        this.positiveInteger(data.modelIds[0]) !== this.positiveInteger(card?.dataset?.taskId) ||
        !this.isPlainObject(attributes) ||
        !this.isPlainObject(contextState) ||
        attributes.target_lane !== null ||
        contextState.target_lane !== null ||
        attributes.target_column !== null ||
        contextState.target_column !== null ||
        (targetColumn !== null && this.positiveInteger(targetColumn) === null) ||
        JSON.stringify(attributes) !== JSON.stringify(contextState)
      ) return null

      return {
        ...data,
        attributes: { ...attributes, target_lane: targetLane, target_column: targetColumn },
        context: {
          ...data.context,
          state: { ...contextState, target_lane: targetLane, target_column: targetColumn }
        }
      }
    },

    watchActionDialogLifecycle() {
      this.stopWatchingActionDialogLifecycle()
      actionExecutedHandler = event => this.handleVirtualActionExecuted(event)
      actionDialogCancelledHandler = event => this.handleVirtualActionCancelled(event)
      window.addEventListener(ACTION_EXECUTED_EVENT, actionExecutedHandler)
      window.addEventListener(ACTION_DIALOG_CANCELLED_EVENT, actionDialogCancelledHandler)
    },

    stopWatchingActionDialogLifecycle() {
      if (actionExecutedHandler !== null) {
        window.removeEventListener(ACTION_EXECUTED_EVENT, actionExecutedHandler)
        actionExecutedHandler = null
      }
      if (actionDialogCancelledHandler !== null) {
        window.removeEventListener(ACTION_DIALOG_CANCELLED_EVENT, actionDialogCancelledHandler)
        actionDialogCancelledHandler = null
      }
    },

    async handleVirtualActionExecuted(event) {
      const action = activeVirtualAction
      if (!this.matchesActiveVirtualAction(event?.detail)) return

      if (event.detail?.response?.type !== 'success') {
        activeVirtualAction = null
        await this.recoverCancelledVirtualAction(action)

        return
      }

      action.succeeded = true
      if (action.boardCommitSettled) this.finishVirtualDispatch()
    },

    async handleVirtualActionCancelled(event) {
      const action = activeVirtualAction
      if (
        action?.succeeded ||
        !this.matchesActiveVirtualAction(event?.detail, true)
      ) return

      activeVirtualAction = null
      await this.recoverCancelledVirtualAction(action)
    },

    matchesActiveVirtualAction(payload, requireActionName = false) {
      if (
        !activeVirtualAction ||
        !this.isPlainObject(payload) ||
        payload.resourceClass !== activeVirtualAction.resourceClass ||
        !Array.isArray(payload.modelIds) ||
        payload.modelIds.length !== 1 ||
        this.positiveInteger(payload.modelIds[0]) !== activeVirtualAction.taskId
      ) return false

      return !requireActionName || (
        payload.actionName === activeVirtualAction.actionName &&
        payload.actionName === VIRTUAL_TRANSITION_ACTION
      )
    },

    async recoverFailedVirtualAction(action, error) {
      if (
        destroyed ||
        activeVirtualAction !== action ||
        action.succeeded
      ) return

      activeVirtualAction = null
      const recoveryToken = { phase: 'refresh' }
      activeDropToken = recoveryToken
      this.restoreVisibleOrder(action.sourceOrder)
      this.setSortablesDisabled(true)
      this.reportError(error)
      await this.refreshProjection(recoveryToken)
      if (destroyed) return
      if (activeDropToken === recoveryToken) activeDropToken = null
      this.finishVirtualDispatch()
    },

    async recoverCancelledVirtualAction(action) {
      if (destroyed) return

      const recoveryToken = { phase: 'refresh' }
      activeDropToken = recoveryToken
      this.restoreVisibleOrder(action.sourceOrder)
      this.setSortablesDisabled(true)
      await this.refreshProjection(recoveryToken)
      if (destroyed) return
      if (activeDropToken === recoveryToken) activeDropToken = null
      this.finishVirtualDispatch()
    },

    restorePendingControlFocus() {
      if (this.saving || !pendingControlFocus) return

      const activeElement = document.activeElement
      const focusWasOrphaned = activeElement === null ||
        activeElement === document.body ||
        activeElement === document.documentElement ||
        !activeElement.isConnected
      if (!focusWasOrphaned) {
        pendingControlFocus = null

        return
      }

      const root = this.boardRoot()
      const card = root?.querySelector(
        `${CARD_SELECTOR}[data-task-id="${pendingControlFocus.taskId}"]`
      )
      if (!card) {
        pendingControlFocus = null

        return
      }

      const targetLaneControl = pendingControlFocus.targetLane
        ? card.querySelector(
            `[data-task-board-action-target="${pendingControlFocus.targetLane}"]:not([hidden])`
        )
        : null
      const opposite = {
        down: 'up',
        'next-lane': 'previous-lane',
        'previous-lane': 'next-lane',
        up: 'down'
      }
      const directions = [
        pendingControlFocus.direction,
        opposite[pendingControlFocus.direction],
        'up',
        'down',
        'previous-lane',
        'next-lane'
      ]
      const control = targetLaneControl ?? [...new Set(directions)]
        .map(direction => card.querySelector(
          `[data-task-board-move-direction="${direction}"]`
        ))
        .find(candidate => candidate && !candidate.disabled)
      const target = control ?? card.querySelector('[data-task-quick-view], a[href]')

      pendingControlFocus = null
      target?.focus()
    },

    async recoverRejectedDrop(error, dropToken) {
      if (destroyed) return

      this.restoreVisibleOrder()
      this.reportError(error)
      if (this.isBoardUnavailable(error)) {
        this.navigateToRecovery()

        return
      }

      await this.refreshProjection(dropToken)
    },

    async refreshProjection(dropToken = null) {
      if (destroyed) return

      const wire = this.livewire()
      if (!wire) return

      if (dropToken && activeDropToken === dropToken) dropToken.phase = 'refresh'

      try {
        await wire.$refresh()
      } catch (error) {
        if (destroyed) return

        if (this.isExpectedRejection(error)) {
          this.navigateToRecovery()

          return
        }

        this.captureUnexpectedError(error)
      }
    },

    navigateToRecovery() {
      if (typeof recoveryUrl !== 'string' || !recoveryUrl.startsWith('/')) return

      const navigate = window.Livewire?.navigate
      if (typeof navigate === 'function') {
        navigate(recoveryUrl)

        return
      }

      window.location.assign(recoveryUrl)
    },

    positiveInteger(value) {
      if (typeof value !== 'string' && typeof value !== 'number') return null

      const number = Number(value)

      return Number.isSafeInteger(number) && number > 0 ? number : null
    },

    isExpectedRejection(error) {
      return error && typeof error === 'object' && EXPECTED_REJECTIONS.includes(Number(error.status))
    },

    isBoardUnavailable(error) {
      return error && typeof error === 'object' && [403, 404].includes(Number(error.status))
    },

    watchLivewireMorphs() {
      stopWatchingMorphs?.()
      stopWatchingMorphs = null

      if (typeof window.Livewire?.hook !== 'function') return

      stopWatchingMorphs = window.Livewire.hook('morph.updating', ({ component, el, toEl }) => {
        if (destroyed || component?.id !== wireId || el !== this.boardRoot() ||
          !toEl?.hasAttribute('data-task-board-root')) return

        // This measured property belongs to the viewport watcher, not the server
        // render. Keep it through attribute morphing without copying other styles.
        const height = el.style.getPropertyValue('--task-board-viewport-height')
        if (height) toEl.style.setProperty('--task-board-viewport-height', height)
      })
    },

    watchLivewireCommits() {
      stopWatchingCommits?.()
      stopWatchingCommits = null

      if (typeof window.Livewire?.hook !== 'function') return

      stopWatchingCommits = window.Livewire.hook('commit', ({ component, commit, succeed, fail }) => {
        if (component?.id !== wireId) return

        const calls = Array.isArray(commit?.calls) ? commit.calls : []
        const generation = this.lanePaginationGeneration()
        const paginationOnly = calls.length > 0 && calls.every(call =>
          call?.method === 'loadMoreLane' && call.params?.[1] === generation) &&
          Object.entries(commit?.updates ?? {}).every(([key, value]) =>
            key === 'clientGen' && Number(value) === generation)
        const root = this.boardRoot()
        const boardId = root?.dataset.boardId
        const boardKind = root?.dataset.taskBoardKind
        const pagination = calls.flatMap(call => {
          if (call?.method !== 'loadMoreLane' || !Array.isArray(call.params)) return []
          const state = lanePagination.get(call.params[0])
          if (!state || state.generation !== call.params[1] ||
            state.generation !== this.lanePaginationGeneration() || !root?.contains(state.list)) return []

          state.consumeScroll()
          if (!state.pending?.active) state.pending = { active: true }
          state.syncFeedback()

          return [{
            token: state.pending
          }]
        })
        const settlePagination = (failed = false) => pagination.forEach(({ token }) => {
          token.failed = failed
          if (!token.automatic) token.active = false
          this.syncLanePaginationFeedback(token)
        })
        const action = activeVirtualAction
        const settlesAction = action !== null &&
          this.isOwnVirtualActionRefreshCommit(commit, action)

        if (typeof fail === 'function') {
          fail(() => queueMicrotask(() => {
            if (destroyed) return
            settlePagination(true)
            if (!settlesAction || activeVirtualAction !== action) return

            action.boardCommitSettled = true
            if (action.succeeded) this.finishVirtualDispatch()
          }))
        }

        succeed(() => queueMicrotask(() => {
          if (destroyed) return
          settlePagination()
          if (settlesAction && activeVirtualAction === action) {
            action.boardCommitSettled = true
            if (action.succeeded) this.finishVirtualDispatch()

            return
          }

          // v4 commit.succeed runs from onRender after the default DOM morph.
          // Reconcile bindings by node identity; retained pagination needs no
          // viewport measurement, focus change or corrective scroll write.
          this.boot({
            pagination: paginationOnly && this.boardRoot() === root &&
              this.lanePaginationGeneration() === generation &&
              root?.dataset.boardId === boardId && root?.dataset.taskBoardKind === boardKind
          })
        }))
      })
    },

    watchLivewireRequests() {
      stopWatchingRequests?.()
      stopWatchingRequests = null

      if (typeof window.Livewire?.hook !== 'function') return

      stopWatchingRequests = window.Livewire.hook('request', ({ payload, fail }) => {
        const dropToken = activeDropToken
        if (
          dropToken &&
          typeof fail === 'function' &&
          this.isOwnDropRequest(payload, dropToken.phase)
        ) {
          fail(({ status, preventDefault }) => {
            if (
              activeDropToken === dropToken &&
              EXPECTED_REJECTIONS.includes(Number(status)) &&
              typeof preventDefault === 'function'
            ) {
              preventDefault()
            }
          })
        }

        const action = activeVirtualAction
        if (
          !action ||
          action.succeeded ||
          typeof fail !== 'function' ||
          !this.isOwnVirtualActionRequest(payload, action)
        ) return

        fail(({ status, content, preventDefault } = {}) => {
          if (activeVirtualAction !== action || action.succeeded) return

          if (
            EXPECTED_REJECTIONS.includes(Number(status)) &&
            typeof preventDefault === 'function'
          ) preventDefault()

          queueMicrotask(() => this.recoverFailedVirtualAction(action, { status, content }))
        })
      })
    },

    isPlainObject(value) {
      return value !== null &&
        typeof value === 'object' &&
        !Array.isArray(value)
    },

    isOwnVirtualActionRefreshCommit(commit, action) {
      const calls = Array.isArray(commit?.calls) ? commit.calls : []

      return calls.some(call => {
        if (
          call?.method !== '__dispatch' ||
          !Array.isArray(call?.params) ||
          call.params[0] !== `table-refresh.${action.resourceClass}`
        ) return false

        const ids = call.params[1]?.ids

        return Array.isArray(ids) && ids.some(
          id => this.positiveInteger(id) === action.taskId
        )
      })
    },

    isOwnDropRequest(payload, phase) {
      const request = this.jsonObject(payload)
      if (!Array.isArray(request?.components)) return false

      const method = phase === 'move'
        ? 'moveTask'
        : phase === 'refresh' ? '$refresh' : null
      if (method === null) return false

      return request.components.some(component => {
        const componentSnapshot = this.jsonObject(component?.snapshot)

        return componentSnapshot?.memo?.id === wireId &&
          Array.isArray(component?.calls) &&
          component.calls.length === 1 &&
          component.calls[0]?.method === method
      })
    },

    isOwnVirtualActionRequest(payload, action) {
      const request = this.jsonObject(payload)
      if (!Array.isArray(request?.components)) return false

      const actionDialogWireId = this.activeActionDialogWireId(action)

      return request.components.some(component => {
        const componentSnapshot = this.jsonObject(component?.snapshot)
        const calls = Array.isArray(component?.calls) ? component.calls : []
        if (
          actionDialogWireId !== null &&
          componentSnapshot?.memo?.id === actionDialogWireId &&
          calls.length > 0
        ) return true

        return calls.some(call => {
          if (
            call?.method !== '__dispatch' ||
            !Array.isArray(call?.params) ||
            call.params[0] !== 'showActionDialog'
          ) return false

          return this.matchesVirtualActionIdentity(call.params[1], action, 'name')
        })
      })
    },

    activeActionDialogWireId(action) {
      const dialog = document.querySelector('#action-dialog[wire\\:id], #action-dialog')
      if (!dialog) return null

      const modelIds = this.jsonObject(dialog.dataset?.actionDialogModelIds)
      if (!this.matchesVirtualActionIdentity({
        actionName: dialog.dataset?.actionDialogActionName,
        modelIds,
        resourceClass: dialog.dataset?.actionDialogResourceClass
      }, action, 'actionName')) return null

      return dialog.closest?.('[wire\\:id]')?.getAttribute?.('wire:id') ?? null
    },

    matchesVirtualActionIdentity(payload, action, actionNameKey) {
      if (
        !this.isPlainObject(payload) ||
        payload.resourceClass !== action.resourceClass ||
        payload[actionNameKey] !== action.actionName ||
        payload[actionNameKey] !== VIRTUAL_TRANSITION_ACTION ||
        !Array.isArray(payload.modelIds) ||
        payload.modelIds.length !== 1
      ) return false

      return this.positiveInteger(payload.modelIds[0]) === action.taskId
    },

    jsonObject(value) {
      if (value && typeof value === 'object') return value
      if (typeof value !== 'string') return null

      try {
        const parsed = JSON.parse(value)

        return parsed && typeof parsed === 'object' ? parsed : null
      } catch {
        return null
      }
    },

    isVirtualBoard() {
      const kind = this.boardRoot()?.dataset?.taskBoardKind

      return virtual === true && ['personal', 'team', 'club'].includes(kind)
    },

    laneIdentifier(list) {
      if (!list?.dataset) return null

      return list.dataset.laneId ?? list.dataset.laneKey ?? null
    },

    allowsProjectedDrop(card, list) {
      const target = list?.dataset?.taskBoardTargetKey ?? this.laneIdentifier(list)
      const taskId = this.positiveInteger(card?.dataset?.taskId)
      const isActiveCard = activeDrag?.taskId === taskId

      if (
        isActiveCard &&
        typeof target === 'string' &&
        target === activeDrag.sourceLane
      ) return true

      const allowed = this.allowsProjectedTarget(card, target)
      if (
        allowed &&
        isActiveCard &&
        typeof target === 'string' &&
        target !== activeDrag.sourceLane
      ) activeDrag.leftSource = true

      return allowed
    },

    allowsProjectedTarget(card, target) {
      const targets = this.jsonObject(card?.dataset?.taskBoardAllowedTargets)

      return Array.isArray(targets) && typeof target === 'string' && targets.includes(target)
    },

    personalStatusLane(value) {
      return ['open', 'in_progress', 'done'].includes(value)
    },

    personalLane(value) {
      return value === 'takeover' || this.personalStatusLane(value)
    },

    reportError(error) {
      window.dispatchEvent(new CustomEvent('notify', {
        detail: { type: 'danger', title: errorTitle, message: errorMessage }
      }))

      this.captureUnexpectedError(error)
    },

    captureUnexpectedError(error) {
      const reportable = this.reportableError(error)
      if (!reportable) return

      Sentry.captureException(reportable)
      console.error(reportable)
    },

    reportableError(error) {
      if (this.isExpectedRejection(error)) return null

      const status = error && typeof error === 'object' ? Number(error.status) : null
      if (!Number.isFinite(status) || status < 500) return null

      if (error instanceof Error) return error

      return new Error(`taskBoardKanban: unexpected Livewire status ${status}`)
    }
  }
}
